/**
 * Расчёт цены заказа — `01-BUSINESS-RULES.md` §1.
 *
 * Модуль намеренно не зависит ни от React, ни от Prisma: одна и та же функция
 * считает цену в браузере (мгновенный отклик формы) и на сервере (то, что
 * реально сохранится). Расхождение показанной и сохранённой цены таким образом
 * невозможно в принципе.
 */
/**
 * Цена одной позиции справочника при заданном способе оплаты.
 * По договору обычно дороже, но у части позиций цены равны, а «не в прайсе» — нули.
 */
export function priceOf(construction, pay) {
    if (!construction)
        return 0;
    return pay === 'По договору' ? construction.priceContractKop : construction.priceCashKop;
}
/**
 * Итоговая цена заказа: базовая конструкция + сумма доп. элементов с учётом количества.
 *
 *   cost = priceOf(конструкция, pay) + Σ priceOf(доп, pay) × qty
 *
 * ВАЖНО (§1): эту функцию обязаны вызывать ВСЕ обработчики, меняющие что-либо
 * из её аргументов — смену конструкции, доп. элементов, способа оплаты, а также
 * смену врача/клиники/филиала, потому что они меняют `pay` через юрлицо.
 * Пропущенный пересчёт при смене клиники был реальной найденной ошибкой демо.
 */
export function computeCostKop(constructionId, pay, extras, catalog, constructionQty = 1) {
    const base = constructionId ? priceOf(catalog.get(constructionId), pay) * qtyOf(constructionQty) : 0;
    const extrasTotal = (extras ?? []).reduce((sum, extra) => {
        return sum + priceOf(catalog.get(extra.id), pay) * extra.qty;
    }, 0);
    return base + extrasTotal;
}
/**
 * Количество базовой конструкции.
 *
 * Ноль и дробь отсекаются: заказ на «ноль аппаратов» бессмыслен, а половина
 * аппарата не изготавливается. Отсутствующее значение — единица: так вели
 * себя все заказы до появления этого поля.
 */
function qtyOf(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return 1;
    return Math.max(1, Math.floor(value));
}
/**
 * Себестоимость заказа: базовая конструкция плюс доп. элементы.
 *
 * База умножается на количество: два аппарата стоят лаборатории вдвое дороже —
 * материалы и работа тратятся на каждый.
 *
 * Доп. элементы СЧИТАЮТСЯ ТОЖЕ, каждый по своему количеству. Раньше их расход
 * пропадал: печать модели тратит пластик и время печатника, а в себестоимость
 * заказа не попадала ни копейки — прибыль по такому заказу выходила завышенной
 * ровно на стоимость допов.
 *
 * На уже сохранённые заказы это не влияет: у каждого свой записанный расход,
 * и задним числом он не пересчитывается.
 */
export function defaultExpenseKop(constructionId, catalog, constructionQty = 1, extras = null) {
    const base = constructionId
        ? (catalog.get(constructionId)?.costKop ?? 0) * qtyOf(constructionQty)
        : 0;
    const extrasTotal = (extras ?? []).reduce((sum, extra) => sum + (catalog.get(extra.id)?.costKop ?? 0) * extra.qty, 0);
    return base + extrasTotal;
}
/**
 * Удобный индекс справочника для передачи в `computeCostKop`.
 * Принимает и полные `Construction`, и любой объект с нужными для расчёта полями.
 */
export function catalogIndex(constructions) {
    return new Map(constructions.map((c) => [c.id, c]));
}
/** Юрлицо, к которому относится филиал заказа. */
export function legalOf(clinics, clinicId, branchId) {
    const clinic = clinics.find((c) => c.id === clinicId);
    const branch = clinic?.branches.find((b) => b.id === branchId);
    return clinic?.legals.find((l) => l.id === branch?.legalId);
}
/**
 * Клиники, доступные для выбора у заказа этого врача (§3).
 *
 * Список ФИЛЬТРУЕТСЯ по `doctor.clinics`, а не показывает все клиники всегда.
 * Исключение: если у врача связи не разобраны (пустой массив — так в исходных
 * данных лаборатории у части врачей), показываются все клиники, иначе заказ
 * на такого врача было бы невозможно оформить.
 */
export function clinicsForDoctor(clinics, doctor) {
    if (!doctor || doctor.clinics.length === 0)
        return [...clinics];
    return clinics.filter((c) => doctor.clinics.includes(c.id));
}
/** Способ оплаты по умолчанию для пары клиника+филиал. */
export function defaultPayFor(clinics, clinicId, branchId, fallback) {
    return legalOf(clinics, clinicId, branchId)?.pay ?? fallback;
}
/**
 * Признак ручного переопределения способа оплаты (§1).
 *
 * Если способ оплаты заказа не совпадает со значением по умолчанию у юрлица —
 * интерфейс обязан показать «Изменено вручную — у клиники по умолчанию «X»»,
 * а не молчаливо разойтись со справочником. Это рабочий сценарий, не ошибка:
 * клиника работает по договору, а конкретный пациент платит наличными.
 */
export function payOverride(clinics, state) {
    const defaultPay = legalOf(clinics, state.clinicId, state.branchId)?.pay ?? null;
    return { overridden: defaultPay !== null && state.pay !== defaultPay, defaultPay };
}
/** Первый филиал клиники — используется как значение по умолчанию при смене клиники. */
function firstBranchOf(clinics, clinicId) {
    return clinics.find((c) => c.id === clinicId)?.branches[0];
}
/**
 * Смена врача.
 *
 * Если новый врач не работает в текущей клинике заказа — клиника автоматически
 * переключается на первую доступную ему, вслед за ней меняется филиал, способ
 * оплаты и ЦЕНА. Если врач в текущей клинике работает — не трогаем ничего,
 * кроме самого врача.
 */
export function applyDoctorChange(state, doctorId, ctx) {
    const doctor = ctx.doctors.find((d) => d.id === doctorId);
    const next = { ...state, doctorId };
    const stillValid = !doctor || doctor.clinics.length === 0 || doctor.clinics.includes(state.clinicId);
    if (stillValid)
        return next;
    const targetClinicId = doctor.clinics[0];
    if (!targetClinicId)
        return next;
    return applyClinicChange(next, targetClinicId, ctx);
}
/**
 * Смена клиники.
 *
 * Здесь была реальная найденная ошибка демо: обновлялся `pay`, но НЕ пересчитывался
 * `cost` — цена оставалась от предыдущего способа оплаты (§1). Поэтому пересчёт
 * стоимости здесь не «на всякий случай», а суть операции.
 */
export function applyClinicChange(state, clinicId, ctx) {
    const branch = firstBranchOf(ctx.clinics, clinicId);
    const branchId = branch?.id ?? '';
    const pay = defaultPayFor(ctx.clinics, clinicId, branchId, state.pay);
    return {
        ...state,
        clinicId,
        branchId,
        pay,
        costKop: computeCostKop(state.constructionId, pay, state.extras, ctx.catalog, state.constructionQty),
    };
}
/** Смена филиала — другое юрлицо, значит другой способ оплаты и другая цена. */
export function applyBranchChange(state, branchId, ctx) {
    const pay = defaultPayFor(ctx.clinics, state.clinicId, branchId, state.pay);
    return {
        ...state,
        branchId,
        pay,
        costKop: computeCostKop(state.constructionId, pay, state.extras, ctx.catalog, state.constructionQty),
    };
}
/**
 * Смена базовой конструкции.
 *
 * Сбрасывает выбранные цвета: цвета привязаны к конкретной конструкции, и
 * у новой конструкции выбор цвета может быть вообще не предусмотрен.
 * Себестоимость подтягивается из справочника.
 */
export function applyConstructionChange(state, constructionId, ctx) {
    /*
     * Количество сбрасывается в единицу, если новая конструкция его не
     * допускает — или если её вовсе не выбрали.
     *
     * Иначе оставшаяся от прежней конструкции двойка продолжала бы умножать
     * цену, а счётчика на экране уже нет: сумма вдвое больше прайсовой и
     * ничего, что бы это объясняло.
     */
    const allowed = ctx.catalog.get(constructionId)?.qtyAdjustable === true;
    const constructionQty = allowed ? state.constructionQty : 1;
    return {
        ...state,
        constructionId,
        constructionQty,
        colors: [],
        costKop: computeCostKop(constructionId, state.pay, state.extras, ctx.catalog, constructionQty),
        expenseKop: defaultExpenseKop(constructionId, ctx.catalog, constructionQty, state.extras),
    };
}
/**
 * Смена количества базовой конструкции.
 *
 * Пересчитывает и цену, и СЕБЕСТОИМОСТЬ: два аппарата стоят лаборатории
 * вдвое дороже. Себестоимость при этом берётся из справочника заново —
 * ручная правка расхода в заказе количеством не масштабируется, потому что
 * неизвестно, за сколько штук её вводили.
 */
export function applyConstructionQtyChange(state, constructionQty, ctx) {
    const qty = qtyOf(constructionQty);
    return {
        ...state,
        constructionQty: qty,
        costKop: computeCostKop(state.constructionId, state.pay, state.extras, ctx.catalog, qty),
        expenseKop: defaultExpenseKop(state.constructionId, ctx.catalog, qty, state.extras),
    };
}
/**
 * Смена набора доп. элементов.
 *
 * Пересчитывает и цену, и СЕБЕСТОИМОСТЬ: у допов теперь есть свой расход,
 * и добавленная печать модели должна прибавить его к затратам по заказу —
 * иначе прибыль по такому заказу окажется завышенной.
 */
export function applyExtrasChange(state, extras, ctx) {
    return {
        ...state,
        extras,
        costKop: computeCostKop(state.constructionId, state.pay, extras, ctx.catalog, state.constructionQty),
        expenseKop: defaultExpenseKop(state.constructionId, ctx.catalog, state.constructionQty, extras),
    };
}
/**
 * Ручная смена способа оплаты (§1).
 *
 * Всегда доступна и всегда пересчитывает цену. Юрлицо при этом не меняется —
 * переопределение живёт на уровне заказа и подсвечивается через `payOverride`.
 */
export function applyPayChange(state, pay, ctx) {
    return {
        ...state,
        pay,
        costKop: computeCostKop(state.constructionId, pay, state.extras, ctx.catalog, state.constructionQty),
    };
}
//# sourceMappingURL=pricing.js.map